import React, { useState, useEffect } from 'react';
import { ChevronRight, ChevronDown, FolderClosed, FolderOpen, File, Loader2 } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { apiRequest } from '@/lib/queryClient';
import { useAppStore } from '@/lib/store';

interface FileItem {
  id: string;
  name: string;
  type: 'file' | 'folder';
  size: number;
  versionId?: string;
  path: string[];
  lastModified: string;
  hasChildren?: boolean;
  children?: FileItem[];
  isExpanded?: boolean;
  isLoading?: boolean;
}

interface HierarchicalFileSelectorProps {
  onSelectionChange: (selectedIds: string[]) => void;
  selectedFiles: string[];
  className?: string;
}

export default function HierarchicalFileSelector({ 
  onSelectionChange, 
  selectedFiles,
  className = ""
}: HierarchicalFileSelectorProps) {
  const [rootFiles, setRootFiles] = useState<FileItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [expandedFolders, setExpandedFolders] = useState<Set<string>>(new Set());
  const [loadingFolders, setLoadingFolders] = useState<Set<string>>(new Set());
  const [lastSelectedIndex, setLastSelectedIndex] = useState<number | null>(null);
  
  // Get selected project from store
  const { selectedProject } = useAppStore();

  // Load root files on component mount when project is available
  useEffect(() => {
    if (selectedProject?.id) {
      console.log('HierarchicalFileSelector: Project changed, reloading files for project:', selectedProject.id);
      // Reset state when project changes
      setRootFiles([]);
      setExpandedFolders(new Set());
      setLoadingFolders(new Set());
      setLastSelectedIndex(null);
      // Load new project's files
      loadFolderContents();
    }
  }, [selectedProject?.id]);

  const loadFolderContents = async (folderId?: string) => {
    try {
      if (!selectedProject?.id) {
        console.error('No project selected');
        return;
      }
      
      if (folderId) {
        setLoadingFolders(prev => new Set(prev).add(folderId));
      } else {
        setIsLoading(true);
      }
      
      // Use project-files endpoint with caching enabled
      const url = folderId 
        ? `/api/project-files?projectId=${selectedProject.id}&folderId=${folderId}&useCache=true`
        : `/api/project-files?projectId=${selectedProject.id}&useCache=true`;
      
      console.log('Loading folder contents from:', url);
      const response = await apiRequest(url);
      
      // Handle both direct array and wrapper object responses
      let fileItems: FileItem[];
      if (Array.isArray(response)) {
        fileItems = response;
      } else if (response && typeof response === 'object' && 'items' in response && Array.isArray((response as any).items)) {
        fileItems = (response as any).items;
      } else {
        console.error('API response is not in expected format:', response);
        return;
      }
      
      console.log('Loaded file items:', fileItems);
      
      if (folderId) {
        // Update specific folder's children
        updateFolderChildren(folderId, fileItems);
      } else {
        // Set root files with proper sorting (folders first, then alphabetical)
        const sortedItems = [...fileItems].sort((a, b) => {
          // Folders come first
          if (a.type === 'folder' && b.type !== 'folder') return -1;
          if (a.type !== 'folder' && b.type === 'folder') return 1;
          // Then sort alphabetically by name
          return a.name.localeCompare(b.name);
        });
        
        setRootFiles(sortedItems.map((file: FileItem) => ({
          ...file,
          isExpanded: false,
          isLoading: false,
          children: []
        })));
      }
    } catch (error) {
      console.error('Error loading folder contents:', error);
    } finally {
      if (folderId) {
        setLoadingFolders(prev => {
          const next = new Set(prev);
          next.delete(folderId);
          return next;
        });
      } else {
        setIsLoading(false);
      }
    }
  };

  const updateFolderChildren = (folderId: string, children: FileItem[]) => {
    const updateFolder = (items: FileItem[]): FileItem[] => {
      return items.map(item => {
        if (item.id === folderId) {
          // Sort children with folders first, then alphabetical
          const sortedChildren = [...children].sort((a, b) => {
            // Folders come first
            if (a.type === 'folder' && b.type !== 'folder') return -1;
            if (a.type !== 'folder' && b.type === 'folder') return 1;
            // Then sort alphabetically by name
            return a.name.localeCompare(b.name);
          });
          
          return {
            ...item,
            children: sortedChildren.map(child => ({
              ...child,
              isExpanded: false,
              isLoading: false,
              children: []
            })),
            isLoading: false,
            isExpanded: true
          };
        }
        if (item.children) {
          return {
            ...item,
            children: updateFolder(item.children)
          };
        }
        return item;
      });
    };

    setRootFiles(updateFolder(rootFiles));
  };

  const toggleFolder = async (folderId: string) => {
    const newExpanded = new Set(expandedFolders);
    
    if (newExpanded.has(folderId)) {
      newExpanded.delete(folderId);
    } else {
      newExpanded.add(folderId);
      // Load children if not already loaded
      await loadFolderContents(folderId);
    }
    
    setExpandedFolders(newExpanded);
  };

  // Helper function to get all children of a folder recursively
  const getAllFolderChildren = async (item: FileItem): Promise<string[]> => {
    const result: string[] = [item.id];
    
    // If it's a folder, try to get children but don't force loading if it takes too long
    if (item.type === 'folder') {
      try {
        // Only expand if not already expanded
        if (!expandedFolders.has(item.id)) {
          console.log(`[FILE-SELECTOR] Loading folder contents for: ${item.name}`);
          setExpandedFolders(prev => new Set([...prev, item.id]));
          
          // Load folder contents with timeout
          await Promise.race([
            loadFolderContents(item.id),
            new Promise(resolve => setTimeout(resolve, 2000)) // 2 second timeout
          ]);
          
          // Quick wait for state update
          await new Promise(resolve => setTimeout(resolve, 100));
        }
        
        // Get the updated item
        const updatedItem = findItemById(rootFiles, item.id);
        
        if (updatedItem && updatedItem.children && updatedItem.children.length > 0) {
          console.log(`[FILE-SELECTOR] Folder "${updatedItem.name}" has ${updatedItem.children.length} children`);
          // Recursively collect all child IDs, but limit depth to prevent infinite loops
          for (const child of updatedItem.children) {
            const childIds = await getAllFolderChildren(child);
            result.push(...childIds);
          }
        } else {
          console.log(`[FILE-SELECTOR] Folder "${item.name}" has no children or failed to load`);
        }
      } catch (error) {
        console.error(`[FILE-SELECTOR] Error loading folder "${item.name}":`, error);
      }
    }
    
    return result;
  };

  const handleFileSelect = async (fileId: string, event: React.MouseEvent, fileIndex: number) => {
    const isShiftClick = event.shiftKey && lastSelectedIndex !== null;
    
    if (isShiftClick) {
      // Shift-click: select range
      const flatFiles = getFlatFileList(rootFiles);
      const startIndex = Math.min(lastSelectedIndex, fileIndex);
      const endIndex = Math.max(lastSelectedIndex, fileIndex);
      
      const rangeIds = flatFiles.slice(startIndex, endIndex + 1).map(f => f.id);
      const newSelected = new Set(selectedFiles);
      
      // Add all files in range to selection
      rangeIds.forEach(id => newSelected.add(id));
      
      onSelectionChange(Array.from(newSelected));
    } else {
      // Simple approach: just toggle the clicked item, let server handle deduplication
      const newSelected = new Set(selectedFiles);
      const isCurrentlySelected = selectedFiles.includes(fileId);
      
      if (isCurrentlySelected) {
        newSelected.delete(fileId);
      } else {
        newSelected.add(fileId);
      }
      
      onSelectionChange(Array.from(newSelected));
      setLastSelectedIndex(fileIndex);
    }
  };

  // Helper function to find item by ID in the tree
  const findItemById = (items: FileItem[], id: string): FileItem | null => {
    for (const item of items) {
      if (item.id === id) return item;
      if (item.children) {
        const found = findItemById(item.children, id);
        if (found) return found;
      }
    }
    return null;
  };

  const getFlatFileList = (items: FileItem[]): FileItem[] => {
    const result: FileItem[] = [];
    
    const traverse = (items: FileItem[]) => {
      items.forEach(item => {
        result.push(item);
        if (item.children && item.isExpanded) {
          traverse(item.children);
        }
      });
    };
    
    traverse(items);
    return result;
  };

  const handleSelectAll = () => {
    const flatFiles = getFlatFileList(rootFiles);
    const allIds = flatFiles.map(f => f.id);
    const allSelected = allIds.every(id => selectedFiles.includes(id));
    
    if (allSelected) {
      onSelectionChange([]);
    } else {
      onSelectionChange(allIds);
    }
  };

  const formatFileSize = (size: number): string => {
    if (size === 0) return '0 KB';
    const units = ['B', 'KB', 'MB', 'GB'];
    const k = 1024;
    const i = Math.floor(Math.log(size) / Math.log(k));
    return parseFloat((size / Math.pow(k, i)).toFixed(1)) + ' ' + units[i];
  };

  const renderFileItem = (item: FileItem, depth: number = 0, fileIndex: number) => {
    const isSelected = selectedFiles.includes(item.id);
    const isExpanded = expandedFolders.has(item.id);
    const isLoadingFolder = loadingFolders.has(item.id);
    
    return (
      <div key={item.id} className="select-none">
        <div
          className={`flex items-center py-2 px-3 hover:bg-gray-50 cursor-pointer ${
            isSelected ? 'bg-blue-50 border-l-2 border-blue-500' : ''
          }`}
          style={{ paddingLeft: `${depth * 20 + 12}px` }}
          onClick={(e) => {
            handleFileSelect(item.id, e, fileIndex).catch(console.error);
          }}
        >
          <div className="flex items-center flex-1 min-w-0">
            {item.type === 'folder' && (
              <Button
                variant="ghost"
                size="sm"
                className="p-0 h-auto mr-1"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleFolder(item.id);
                }}
              >
                {isLoadingFolder ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : isExpanded ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </Button>
            )}
            
            <Checkbox
              checked={isSelected}
              onChange={() => {}}
              className="mr-3"
            />
            
            {item.type === 'folder' ? (
              isExpanded ? (
                <FolderOpen className="h-4 w-4 text-blue-600 mr-2" />
              ) : (
                <FolderClosed className="h-4 w-4 text-blue-600 mr-2" />
              )
            ) : (
              <File className="h-4 w-4 text-gray-500 mr-2" />
            )}
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <span className="font-medium text-gray-900 truncate">{item.name}</span>
                <div className="flex items-center space-x-4 text-sm text-gray-500">
                  {item.type === 'file' && (
                    <span className="font-mono">{formatFileSize(item.size)}</span>
                  )}
                  {item.type === 'folder' && (
                    <span className="text-blue-600">📁</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        
        {/* Render children if expanded */}
        {item.type === 'folder' && isExpanded && item.children && (
          <div>
            {item.children.map((child, childIndex) => 
              renderFileItem(child, depth + 1, fileIndex + childIndex + 1)
            )}
          </div>
        )}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 mr-2 animate-spin text-blue-600" />
        <span className="text-gray-600">Loading files...</span>
      </div>
    );
  }

  const flatFiles = getFlatFileList(rootFiles);
  const allSelected = flatFiles.length > 0 && flatFiles.every(f => selectedFiles.includes(f.id));

  return (
    <div className={`border border-gray-200 rounded-lg ${className}`}>
      {/* Header with Select All */}
      <div className="flex items-center justify-between p-3 bg-gray-50 border-b border-gray-200">
        <div className="flex items-center space-x-3">
          <Checkbox
            checked={allSelected}
            onCheckedChange={handleSelectAll}
          />
          <span className="font-medium text-gray-700">
            Select All ({flatFiles.length} items)
          </span>
        </div>
        <span className="text-sm text-gray-500">
          {selectedFiles.length} selected
        </span>
      </div>
      
      {/* File Tree */}
      <div className="max-h-96 overflow-y-auto">
        {rootFiles.map((file, index) => renderFileItem(file, 0, index))}
      </div>
      
      {rootFiles.length === 0 && (
        <div className="p-8 text-center text-gray-500">
          <File className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <p>No files found in this folder</p>
        </div>
      )}
    </div>
  );
}